import CardFillDetailDialog from '@/components/client/card-fill-detail-dialog';
import CopyablePhone from '@/components/copyable-phone';
import CompactEntityCard from '@/components/client/compact-entity-card';
import PhoneStatusBadge from '@/components/client/phone-status-badge';
import SendUssdModal from '@/pages/client/send-ussd-modal';
import type { IClient, IPhone } from '@/entities/client.entity';
import { getLatestPhoneLookupJob, getPhoneLookupStatusLabel, isJobActive } from '@/lib/card-fill-meta';
import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { getPhoneStatusBorderClass } from '@/lib/phone-status-border';
import { cn } from '@/lib/utils';
import {
	AsteriskIcon,
	InfoIcon,
	PhoneIcon,
	RotateCcwIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const RESOLVE_REASON_LABEL: Record<string, string> = {
	unknown_operator: 'Неизвестный оператор',
	skipped_perfectum: 'Perfectum: USSD недоступен',
	ussd_timeout: 'Таймаут USSD',
	ussd_failure: 'Ошибка USSD',
	parse_failed: 'Не удалось распознать номер',
	max_attempts: 'Исчерпаны попытки',
	ussd_multi_candidate: 'USSD: выбран первый из нескольких номеров',
};

type PhoneRow = Pick<IPhone, 'name' | 'number' | 'simSlotIndex' | 'isDefault' | 'countryIso' | 'numberResolveReason'> & {
	id?: number;
	subId?: string;
	status?: number;
	clientId?: string;
};

function SimMetaChips({ phone }: { phone: PhoneRow }) {
	const chips: string[] = [];
	if (phone.simSlotIndex != null) chips.push(`slot ${phone.simSlotIndex}`);
	if (phone.countryIso) chips.push(phone.countryIso.toUpperCase());
	if (phone.isDefault) chips.push('основная');

	if (!chips.length) return null;

	return (
		<div className='flex flex-wrap gap-1 mt-0.5'>
			{chips.map(chip => (
				<span
					key={chip}
					className='inline-flex items-center rounded-md border border-border bg-background/60 px-1.5 py-0 text-[10px] text-muted-foreground'
				>
					{chip}
				</span>
			))}
		</div>
	);
}

function CompactPhoneCard({ phone, onCopy }: { phone: PhoneRow; onCopy: (text: string) => void }) {
	return (
		<CompactEntityCard borderAccentClass={getPhoneStatusBorderClass(phone.status)}>
			<div className='flex items-center gap-1.5 min-w-0'>
				<div className='flex size-5 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground'>
					<PhoneIcon size={12} />
				</div>
				<span className='text-xs font-medium break-all'>{phone.name || 'SIM'}</span>
			</div>

			<SimMetaChips phone={phone} />

			{phone.number ? (
				<CopyablePhone
					value={phone.number}
					onCopy={onCopy}
					className='mt-1 block w-full text-sm font-semibold text-chart-3 hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 break-all'
				/>
			) : phone.status != null ? (
				<div className='mt-1'>
					<PhoneStatusBadge status={phone.status} />
				</div>
			) : (
				<p className='mt-1 text-xs text-muted-foreground'>Нет номера</p>
			)}
		</CompactEntityCard>
	);
}

interface ClientPhonesProps {
	client?: IClient;
	phones: PhoneRow[] | undefined | null;
	onCopy: (text: string) => void;
	/** ussd = detail UX; simple = numbers only; compact = clients list mini-cards */
	variant?: 'ussd' | 'simple' | 'compact';
}

function retrySimResolve(phone: PhoneRow, clientId?: string) {
	if (phone.id == null && !clientId) return;
	socket.emit(SOCKET_EVENTS.retrySimNumberResolve, {
		phoneId: phone.id,
		clientId: clientId ?? phone.clientId,
	});
	toast.message('Повторное определение номера запущено');
}

function toFullPhone(phone: PhoneRow, client?: IClient): IPhone | null {
	if (phone.id == null) return null;
	return {
		id: phone.id,
		subId: phone.subId ?? '',
		name: phone.name,
		number: phone.number,
		buildId: client?.buildId ?? '',
		clientId: phone.clientId ?? client?.id ?? '',
		createdAt: '',
		status: (phone.status ?? 0) as IPhone['status'],
		simSlotIndex: phone.simSlotIndex,
		isDefault: phone.isDefault,
		countryIso: phone.countryIso,
		numberResolveReason: phone.numberResolveReason,
	};
}

export default function ClientPhones({ client, phones, onCopy, variant = 'ussd' }: ClientPhonesProps) {
	const [ussdOpen, setUssdOpen] = useState(false);
	const [initialPhoneId, setInitialPhoneId] = useState<number | undefined>(undefined);
	const [detailPhone, setDetailPhone] = useState<IPhone | null>(null);
	const list = phones?.filter(p => p) ?? [];

	function openUssdModal(phoneId: number) {
		setInitialPhoneId(phoneId);
		setUssdOpen(true);
	}

	function handleUssdOpenChange(open: boolean) {
		setUssdOpen(open);
		if (!open) setInitialPhoneId(undefined);
	}

	const sortedList = list.slice().sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

	return (
		<div className='flex flex-col gap-1.5'>
			<div className='flex items-center gap-1 min-w-0'>
				<PhoneIcon size={14} className='opacity-40 shrink-0' />
				<span className='text-sm text-muted-foreground'>
					Номера
					{list.length > 0 ? <span className='tabular-nums'> ({list.length})</span> : null}
				</span>
			</div>

			<div className='flex flex-col gap-1.5'>
				{list.length ? (
					sortedList.map((phone, i) => {
						if (variant === 'compact') {
							return <CompactPhoneCard key={phone.id ?? phone.subId ?? i} phone={phone} onCopy={onCopy} />;
						}

						if (variant === 'simple') {
							if (!phone.number) return null;
							return (
								<div key={`${phone.subId ?? phone.id ?? i}`} className='text-left text-sm px-0.5'>
									{phone.name ? <span className='text-muted-foreground font-medium mr-1'>{phone.name}:</span> : null}
									<CopyablePhone
										value={phone.number}
										onCopy={onCopy}
										className='inline rounded-xs px-0.5 active:bg-accent break-all'
									/>
								</div>
							);
						}

						const latestLookup =
							phone.id != null ? getLatestPhoneLookupJob(client?.cardFillJobs, phone.id) : null;
						const lookupRunning = isJobActive(latestLookup);
						const lookupStatusLabel =
							phone.id != null ? getPhoneLookupStatusLabel(client?.cardFillJobs, phone.id) : null;

						return (
							<div
								key={phone.id ?? i}
								className={cn(
									'rounded-xl border border-border bg-card p-2.5 shadow-sm border-l-[3px]',
									getPhoneStatusBorderClass(phone.status),
								)}
							>
								<div className='flex items-start justify-between gap-2'>
									<div className='flex items-center gap-1.5 min-w-0'>
										<div className='flex size-6 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground'>
											<PhoneIcon size={13} />
										</div>
										<span className='text-sm font-medium truncate'>{phone.name}</span>
									</div>
									{phone.id != null ? (
										<div className='flex shrink-0 gap-1'>
											<Button
												variant='ghost'
												size='sm'
												className='h-7 px-2 text-xs'
												type='button'
												onClick={() => openUssdModal(phone.id!)}
											>
												<AsteriskIcon size={13} />
												USSD
											</Button>
											<Button
												variant='ghost'
												size='sm'
												className='h-7 px-2 text-xs'
												type='button'
												onClick={() => {
													const full = toFullPhone(phone, client);
													if (full) setDetailPhone(full);
												}}
											>
												<InfoIcon size={13} />
											</Button>
										</div>
									) : null}
								</div>

								<SimMetaChips phone={phone} />

								{phone.number ? (
									<>
										<CopyablePhone
											value={phone.number}
											onCopy={onCopy}
											className='mt-1.5 block w-full text-sm font-semibold text-chart-3 hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 truncate'
										/>
										{lookupStatusLabel ? (
											<p
												className={cn(
													'mt-1 text-[11px]',
													latestLookup?.status === 'failed'
														? 'text-destructive/90'
														: lookupRunning
															? 'text-chart-3'
															: 'text-muted-foreground',
												)}
											>
												Вбив: {lookupStatusLabel}
											</p>
										) : null}
										{phone.numberResolveReason === 'ussd_multi_candidate' ? (
											<p className='mt-1 text-[11px] text-amber-600 dark:text-amber-500'>
												{RESOLVE_REASON_LABEL.ussd_multi_candidate}
											</p>
										) : null}
									</>
								) : phone.status != null ? (
									<div className='mt-1.5 flex flex-col gap-1'>
										<PhoneStatusBadge status={phone.status} />
										{phone.status === 3 && phone.numberResolveReason ? (
											<p className='text-[11px] text-destructive/90'>
												{RESOLVE_REASON_LABEL[phone.numberResolveReason] ?? phone.numberResolveReason}
											</p>
										) : null}
										{phone.status === 3 && phone.id != null ? (
											<Button
												variant='outline'
												size='sm'
												className='h-7 w-fit px-2 text-xs'
												type='button'
												onClick={() => retrySimResolve(phone, client?.id)}
											>
												<RotateCcwIcon size={12} className='mr-1' />
												Повторить
											</Button>
										) : null}
									</div>
								) : (
									<p className='mt-1.5 text-xs text-muted-foreground'>Нет номера</p>
								)}
							</div>
						);
					})
				) : (
					<span className='opacity-60 text-sm'>
						{variant === 'compact' ? 'Нет SIM' : variant === 'simple' ? 'Нет номера' : 'Нет номеров'}
					</span>
				)}
			</div>

			{variant === 'ussd' && client ? (
				<SendUssdModal
					client={client}
					open={ussdOpen}
					onOpenChange={handleUssdOpenChange}
					initialPhoneId={initialPhoneId}
				/>
			) : null}

			<CardFillDetailDialog
				target={detailPhone ? { mode: 'phone', phone: detailPhone } : null}
				jobs={client?.cardFillJobs}
				open={detailPhone != null}
				onOpenChange={open => {
					if (!open) setDetailPhone(null);
				}}
			/>
		</div>
	);
}
